import styled from "@emotion/styled/macro";
import { css } from "@emotion/react";

/* ─── Page Wrapper ─── */
export const TreatPageWrapper = styled.div`
    padding: 0 20px;
    width: 100%;
    @media (max-width: 768px) { padding: 0 12px; }
`;

/* ─── Header ─── */
export const TreatHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    background: linear-gradient(135deg, #6a1b9a 0%, #4a148c 100%);
    color: #fff;
    border-radius: 10px;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;
    box-shadow: 0 4px 12px rgba(106,27,154,0.25);
    @media (max-width: 768px) { flex-direction: column; align-items: flex-start; padding: 14px 16px; }
`;

export const TreatHeaderTitle = styled.div`
    h3 { margin: 0 0 4px 0; font-size: 1.15rem; font-weight: 600; }
    p { margin: 0; font-size: 0.82rem; opacity: 0.85; }
`;

export const TreatHeaderStats = styled.div`
    display: flex; gap: 12px; flex-wrap: wrap;
    @media (max-width: 768px) { gap: 8px; width: 100%; }
`;

export const StatBadge = styled.div`
    display: flex; align-items: center; gap: 5px;
    padding: 5px 12px; background: rgba(255,255,255,0.15);
    border-radius: 18px; font-size: 0.78rem; font-weight: 500;
    span.count { font-weight: 700; font-size: 0.95rem; }
`;

/* ─── Progress ─── */
export const ProgressSection = styled.div`
    margin-bottom: 20px; background: #fff; border-radius: 10px;
    padding: 14px 20px; box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    border: 1px solid #e8edf2;
`;

export const ProgressLabel = styled.div`
    display: flex; justify-content: space-between; margin-bottom: 8px;
    font-size: 0.82rem; color: #546e7a;
    span.percent { font-weight: 600; color: #6a1b9a; }
`;

export const BarOuter = styled.div`
    width: 100%; height: 8px; background: #e8edf2; border-radius: 4px; overflow: hidden;
`;

export const BarInner = styled.div`
    height: 100%; background: linear-gradient(90deg, #6a1b9a, #ab47bc);
    border-radius: 4px; transition: width 0.6s ease;
    width: ${function(p) { return p.percent || 0; }}%;
`;

/* ─── Due Now Banner ─── */
export const DueNowBanner = styled.div`
    display: flex; align-items: center; gap: 12px;
    padding: 12px 18px; background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
    border-left: 4px solid #ff9800; border-radius: 8px;
    margin-bottom: 20px; box-shadow: 0 1px 4px rgba(0,0,0,0.05);
    flex-wrap: wrap;
    .due-count { font-size: 1.8rem; font-weight: 700; color: #e65100; min-width: 40px; text-align: center; }
    .due-text { flex: 1; }
    .due-title { font-size: 0.85rem; font-weight: 600; color: #e65100; }
    .due-desc { font-size: 0.78rem; color: #795548; }
`;

/* ─── Conditional toggle ─── */
export const ConditionalToggle = styled.div`
    display: flex; align-items: center; gap: 8px; margin-bottom: 16px;
    font-size: 0.82rem; color: #546e7a;
`;

export const ToggleSwitch = styled.button`
    width: 40px; height: 22px; border-radius: 11px; border: none;
    cursor: pointer; position: relative; transition: background 0.2s;
    background: ${function(p) { return p.active ? "#005a8b" : "#cfd8dc"; }};
    &::after {
        content: ""; position: absolute; top: 2px;
        left: ${function(p) { return p.active ? "20px" : "2px"; }};
        width: 18px; height: 18px; border-radius: 50%;
        background: #fff; transition: left 0.2s;
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    }
`;

/* ═══════════ Matrix Table ═══════════ */
export const MatrixWrapper = styled.div`
    background: #fff; border-radius: 10px; border: 1px solid #e8edf2;
    overflow-x: auto; margin-bottom: 24px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;

export const MatrixTable = styled.table`
    width: 100%; border-collapse: collapse; min-width: 700px;
`;

export const MatrixThead = styled.thead`
    background: #fbfbfb;
    th {
        padding: 10px 12px; font-size: 0.72rem; font-weight: 600;
        color: #005a8b; text-transform: uppercase; letter-spacing: 0.5px;
        text-align: center; border-bottom: 1px solid #bababa;
        white-space: nowrap;
    }
    th:first-of-type { text-align: left; min-width: 200px; }
`;

export const MatrixTbody = styled.tbody`
    tr {
        border-bottom: 1px solid #f0f2f5;
        transition: background 0.15s;
        &:hover { background: #f5f5f5; }
        &:last-child { border-bottom: none; }
    }
    td {
        padding: 10px 12px;
        text-align: center;
        vertical-align: middle;
        font-size: 0.84rem;
    }
    td:first-of-type { text-align: left; }
`;

/* ─── Intervention row label ─── */
export const IntervLabel = styled.div`
    display: flex; align-items: center; gap: 8px;
    .iv-icon { font-size: 1.1rem; }
    .iv-name { font-weight: 600; color: #263238; font-size: 0.85rem; }
    .iv-sub { font-size: 0.7rem; color: #90a4ae; }
`;

/* ─── Dose cell ─── */
export const DoseCell = styled.div`
    display: inline-flex; align-items: center; justify-content: center;
    width: 32px; height: 32px; border-radius: 6px; font-size: 0.8rem;
    font-weight: 700; cursor: ${function(p) { return p.clickable ? "pointer" : "default"; }};
    transition: transform 0.15s, box-shadow 0.15s;
    ${function(p) {
        if (p.given) return css`
            background: #e8f5e9; color: #2e7d32; border: 1.5px solid #43a047;
        `;
        if (p.isDue) return css`
            background: #fff3e0; color: #e65100; border: 1.5px dashed #ff9800;
            animation: pulse 2s infinite;
            @keyframes pulse {
                0%, 100% { box-shadow: 0 0 0 0 rgba(255,152,0,0.3); }
                50% { box-shadow: 0 0 0 4px rgba(255,152,0,0.1); }
            }
        `;
        return css`
            background: #f5f5f5; color: #bdbdbd; border: 1.5px solid #e0e0e0;
        `;
    }}
    ${function(p) { return p.clickable ? css`
        &:hover { transform: scale(1.15); box-shadow: 0 2px 8px rgba(0,0,0,0.12); }
    ` : ""; }}
`;

/* ─── Completion column ─── */
export const CompletionCell = styled.div`
    display: flex; align-items: center; gap: 6px; justify-content: center;
`;

export const MiniBar = styled.div`
    width: 50px; height: 5px; background: #e0e0e0; border-radius: 3px; overflow: hidden;
`;

export const MiniBarFill = styled.div`
    height: 100%; border-radius: 3px; transition: width 0.4s;
    width: ${function(p) { return p.percent || 0; }}%;
    background: ${function(p) {
        if (p.percent >= 100) return "#43a047";
        if (p.percent > 0) return "#ffa000";
        return "#e0e0e0";
    }};
`;

/* ─── Card View (mobile-friendly alternative) ─── */
export const CardGrid = styled.div`
    display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; margin-bottom: 24px;
    @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

export const InterventionCard = styled.div`
    background: #fff; border: 1.5px solid #e8edf2; border-radius: 10px;
    padding: 16px; transition: box-shadow 0.2s;
    &:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.06); }
    ${function(p) { return p.isComplete ? css`
        border-color: #43a047; background: #fafff9;
    ` : ""; }}
`;

export const CardHeader = styled.div`
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 12px; gap: 8px;
`;

export const CardIcon = styled.div`
    font-size: 1.4rem;
`;

export const CardTitle = styled.div`
    flex: 1;
    .ct-name { font-size: 0.9rem; font-weight: 600; color: #263238; }
    .ct-sub { font-size: 0.72rem; color: #90a4ae; }
`;

export const CardBadge = styled.span`
    padding: 3px 8px; border-radius: 10px; font-size: 0.68rem;
    font-weight: 600; text-transform: uppercase;
    ${function(p) {
        if (p.complete) return css`background: #e8f5e9; color: #2e7d32;`;
        if (p.due) return css`background: #fff3e0; color: #e65100;`;
        return css`background: #f5f5f5; color: #757575;`;
    }}
`;

export const DoseRow = styled.div`
    display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 10px;
`;

export const CardNotes = styled.div`
    font-size: 0.72rem; color: #90a4ae; font-style: italic;
    margin-top: 8px; line-height: 1.4;
`;

/* ─── Dialog ─── */
export const DialogOverlay = styled.div`
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.45); z-index: 1200;
    display: flex; align-items: center; justify-content: center; padding: 20px;
`;

export const DialogBox = styled.div`
    background: #fff; border-radius: 12px; width: 100%; max-width: 460px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.15); overflow: hidden;
`;

export const DialogHeader = styled.div`
    padding: 18px 24px; background: #fbfbfb; border-bottom: 1px solid #bababa;
    display: flex; align-items: center; justify-content: space-between;
    h4 { margin: 0; font-size: 1rem; font-weight: 600; color: #005a8b; }
`;

export const DialogBody = styled.div` padding: 24px; `;

export const DialogFooter = styled.div`
    padding: 16px 24px; border-top: 1px solid #e8edf2;
    display: flex; justify-content: flex-end; gap: 10px;
`;

export const DialogCloseBtn = styled.button`
    background: none; border: none; font-size: 1.2rem;
    cursor: pointer; color: #90a4ae; padding: 0; line-height: 1;
    &:hover { color: #546e7a; }
`;

export const DialogBtn = styled.button`
    padding: 8px 20px; border-radius: 6px; font-size: 0.85rem;
    font-weight: 600; cursor: pointer; border: none; transition: all 0.2s;
    ${function(p) {
        if (p.variant === "primary") return css`
            background: #005a8b; color: #fff;
            &:hover { background: #004080; }
            &:disabled { background: #90a4ae; cursor: not-allowed; }
        `;
        return css`
            background: #f0f2f5; color: #546e7a;
            &:hover { background: #e0e0e0; }
        `;
    }}
`;

export const FormField = styled.div`
    margin-bottom: 16px;
    label { display: block; font-size: 0.78rem; font-weight: 600; color: #546e7a;
        text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
    input, textarea {
        width: 100%; padding: 10px 12px; border: 1px solid #cfd8dc;
        border-radius: 6px; font-size: 0.88rem; color: #263238; outline: none;
        transition: border 0.2s;
        &:focus { border-color: #6a1b9a; box-shadow: 0 0 0 2px rgba(106,27,154,0.12); }
    }
    textarea { resize: vertical; min-height: 60px; }
`;

export const InfoText = styled.div`
    padding: 10px 14px; background: #f5f0fa; border-radius: 6px;
    font-size: 0.88rem; color: #263238;
`;

/* ─── View toggle ─── */
export const ViewToggle = styled.div`
    display: inline-flex; background: #f0f2f5; border-radius: 8px;
    padding: 3px; margin-bottom: 16px; gap: 2px;
`;

export const ViewToggleBtn = styled.button`
    padding: 7px 16px; border: none; border-radius: 6px; font-size: 0.8rem;
    font-weight: 500; cursor: pointer; transition: all 0.2s;
    background: ${function(p) { return p.active ? "#6a1b9a" : "transparent"; }};
    color: ${function(p) { return p.active ? "#fff" : "#546e7a"; }};
    &:hover { background: ${function(p) { return p.active ? "#6a1b9a" : "#e0e0e0"; }}; }
`;

/* ─── Empty / Loading ─── */
export const EmptyState = styled.div`
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; padding: 60px 20px; text-align: center; color: #90a4ae;
    .empty-icon { font-size: 3rem; margin-bottom: 16px; }
    .empty-title { font-size: 1.05rem; font-weight: 600; color: #546e7a; margin-bottom: 8px; }
    .empty-desc { font-size: 0.85rem; max-width: 380px; line-height: 1.5; }
`;

export const LoadingWrapper = styled.div`
    display: flex; align-items: center; justify-content: center;
    padding: 60px 20px; color: #90a4ae; font-size: 0.9rem; gap: 10px;
`;

export const GoToBookingBtn = styled.button`
    margin-top: 20px; padding: 10px 24px; background: #6a1b9a; color: #fff;
    border: none; border-radius: 8px; font-size: 0.9rem; font-weight: 600;
    cursor: pointer; transition: background 0.2s;
    &:hover { background: #4a148c; }
`;
